import React, { useState, useRef } from 'react';
import {
  Textarea,
  Menu,
  TextInput,
  Text,
  Box,
  Badge,
  Group,
  ScrollArea,
  ActionIcon,
} from '@mantine/core';
import { IconSearch, IconPlus } from '@tabler/icons';

interface AvailableField {
  key: string;
  label: string;
  group: 'Original' | 'Mapped';
  type: string;
}

interface TemplateInputProps {
  value: string;
  onChange: (value: string) => void;
  availableFields: AvailableField[];
  placeholder?: string;
}

export const TemplateInput: React.FC<TemplateInputProps> = ({
  value,
  onChange,
  availableFields,
  placeholder = 'Type {{ to insert a field...',
}) => {
  const [showPicker, setShowPicker] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Group fields by group
  const groupedFields = availableFields.reduce(
    (acc, field) => {
      if (!acc[field.group]) {
        acc[field.group] = [];
      }
      acc[field.group].push(field);
      return acc;
    },
    {} as Record<string, AvailableField[]>,
  );

  // Filter fields based on search term
  const filteredFields = availableFields.filter(
    (field) =>
      field.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      field.key.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Handle textarea change
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart || 0;
    onChange(newValue);

    // Always update cursor position to keep it in sync
    setCursorPosition(cursorPos);

    // Check if user just typed {{
    const textBeforeCursor = newValue.substring(0, cursorPos);
    const lastTwoChars = textBeforeCursor.slice(-2);

    if (lastTwoChars === '{{') {
      console.log('[TemplateInput] Detected {{, opening picker');
      setShowPicker(true);
      setSearchTerm('');
    } else if (showPicker) {
      // Check if we're still in a {{ context or if user moved away
      const lastOpenIndex = textBeforeCursor.lastIndexOf('{{');
      const textAfterLastOpen = textBeforeCursor.substring(lastOpenIndex + 2);
      // Close picker if user moved cursor away from {{ or if there's already a closing }}
      if (lastOpenIndex === -1 || textAfterLastOpen.includes('}}')) {
        console.log('[TemplateInput] Closing picker - moved away from {{');
        setShowPicker(false);
      }
    }
  };

  // Handle field selection
  const handleFieldSelect = (field: AvailableField) => {
    // Ensure we have a valid field key - use key, or fallback to label
    const fieldKey = field.key || field.label;
    if (!fieldKey) {
      console.error(
        '[TemplateInput] Field selection error: field.key and field.label are both undefined',
        field,
      );
      setShowPicker(false);
      setSearchTerm('');
      return;
    }

    console.log('[TemplateInput] Field selected:', { fieldKey, field });

    // Get current value and cursor position from textarea (more reliable than state)
    const currentValue = textareaRef.current?.value || value;
    const currentCursorPos = textareaRef.current?.selectionStart || cursorPosition;

    console.log('[TemplateInput] Current state:', {
      currentValue,
      currentCursorPos,
      value,
      cursorPosition,
    });

    const textBeforeCursor = currentValue.substring(0, currentCursorPos);
    const textAfterCursor = currentValue.substring(currentCursorPos);

    // Find the last {{ before cursor
    const lastOpenIndex = textBeforeCursor.lastIndexOf('{{');

    console.log('[TemplateInput] Replacement logic:', {
      lastOpenIndex,
      textBeforeCursor: textBeforeCursor.slice(-10),
      textAfterCursor: textAfterCursor.slice(0, 10),
    });

    if (lastOpenIndex !== -1) {
      // Get text between {{ and cursor (should be empty or partial field name)
      const textBetween = textBeforeCursor.substring(lastOpenIndex + 2);

      // Replace everything from {{ to cursor with {{fieldKey}}
      const before = currentValue.substring(0, lastOpenIndex);
      const after = textAfterCursor;
      const fieldPlaceholder = `{{${fieldKey}}}`;
      const newValue = `${before}${fieldPlaceholder}${after}`;

      console.log('[TemplateInput] Creating new value:', {
        before: before.slice(-10),
        fieldPlaceholder,
        after: after.slice(0, 10),
        newValue,
      });

      onChange(newValue);

      // Set cursor after the inserted field
      const newCursorPos = lastOpenIndex + fieldPlaceholder.length;
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
          textareaRef.current.focus();
        }
      }, 0);
    } else {
      console.warn('[TemplateInput] No {{ found before cursor, cannot insert field');
    }

    setShowPicker(false);
    setSearchTerm('');
  };

  const handleOpenPicker = () => {
    // Insert {{ at cursor position and open picker
    const currentValue = textareaRef.current?.value || value;
    const currentCursorPos = textareaRef.current?.selectionStart || cursorPosition;

    const before = currentValue.substring(0, currentCursorPos);
    const after = currentValue.substring(currentCursorPos);
    // Only insert {{, not }}
    const newValue = before + '{{' + after;

    onChange(newValue);
    setCursorPosition(currentCursorPos + 2);
    setShowPicker(true);
    setSearchTerm('');

    // Update cursor position in textarea
    setTimeout(() => {
      if (textareaRef.current) {
        const newPos = currentCursorPos + 2;
        textareaRef.current.setSelectionRange(newPos, newPos);
        textareaRef.current.focus();
      }
    }, 0);
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onSelect={(e) => {
          // Update cursor position when user clicks or moves cursor
          const target = e.target as HTMLTextAreaElement;
          setCursorPosition(target.selectionStart || 0);
        }}
        placeholder={placeholder}
        minRows={4}
        autosize
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setShowPicker(false);
          }
        }}
        styles={{
          input: {
            paddingRight: '32px', // Make room for the plus icon
          },
        }}
      />
      <ActionIcon
        size="sm"
        variant="subtle"
        color="gray"
        sx={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          zIndex: 1,
        }}
        onClick={handleOpenPicker}
        title="Insert field"
      >
        <IconPlus size={16} />
      </ActionIcon>

      {showPicker && (
        <Menu
          opened={showPicker}
          onClose={() => setShowPicker(false)}
          position="left-start"
          withinPortal
          withArrow
        >
          <Menu.Target>
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                opacity: 0,
                pointerEvents: 'none',
                width: '1px',
                height: '1px',
              }}
            />
          </Menu.Target>
          <Menu.Dropdown
            sx={{
              maxWidth: 400,
              padding: 0,
              maxHeight: 300,
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            <Box
              p={5}
              sx={{
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
                zIndex: 10,
                borderBottom: '1px solid #e9ecef',
              }}
            >
              <TextInput
                icon={<IconSearch size={14} />}
                placeholder="Search fields..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.currentTarget.value)}
                size="sm"
                autoFocus
              />
            </Box>

            {filteredFields.length === 0 ? (
              <Box p="sm">
                <Text size="sm" color="dimmed" align="center">
                  No fields found
                </Text>
              </Box>
            ) : (
              <Box>
                {Object.entries(groupedFields).map(([group, fields]) => {
                  const groupFields = fields.filter((field) =>
                    filteredFields.some((f) => f.key === field.key),
                  );

                  if (groupFields.length === 0) return null;

                  return (
                    <Box key={group}>
                      <Box
                        px="xs"
                        py={5}
                        sx={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}
                      >
                        <Text size="xs" weight={600} color="dimmed">
                          {group} Fields
                        </Text>
                      </Box>
                      {groupFields.map((field) => (
                        <Menu.Item
                          key={field.key || field.label}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('[TemplateInput] Menu.Item clicked:', field);
                            handleFieldSelect(field);
                          }}
                        >
                          <Group spacing="xs">
                            <Text size="sm">{field.label}</Text>
                            <Badge size="xs" variant="light" color="gray">
                              {field.type}
                            </Badge>
                          </Group>
                          <Text size="xs" color="dimmed">
                            {field.key || '(no key)'}
                          </Text>
                        </Menu.Item>
                      ))}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Menu.Dropdown>
        </Menu>
      )}
    </Box>
  );
};
